/**
 * src/api/engineApi.js
 *
 * Engine job management — Azure-backed persistence as of #1019 Phase 3.
 *
 * Jobs and run logs live in the engine_jobs and engine_run_log SQL tables.
 * The endpoints enforce ENG-B-005: tenant users only see/write their own
 * tenant-scoped jobs (plus visible super-admin templates), super-admin
 * sees all. Server-side timer rows (scope='server') are seeded by the
 * timer functions themselves and surface here read-only.
 */

import { generateId, now } from '../utils/formatting.js';
import {
  azureGet,
  azurePost,
  azureFetch,
  azureEnvGet,
  azureEnvPost,
  azureEnvDelete,
} from './azureClient.js';

// ── Jobs ──────────────────────────────────────────────────────────────────────
// Every function below accepts an optional trailing `env` argument
// ('production' | 'test' | 'development'). When omitted, it operates on the
// current tenant's Function App via the JWT-scoped azureGet/azurePost calls
// (used by the tenant Engine tab). When passed, it routes to the named
// environment's Function App for the super-admin Engine tab's cross-env view
// — this is the single implementation for both callers.

/**
 * Load all jobs visible to the current tenant.
 *   - own tenant-scoped jobs
 *   - super-admin templates marked visibleToTenant
 *
 * Deliberately does not catch: useEngine.js's load effect distinguishes "the
 * request failed" from "this tenant genuinely has no jobs" (#5580) — an admin
 * seeing an empty Engine tab needs to know which one it is, since the two read
 * as very different things (nothing configured vs. something is broken). A
 * blanket catch-and-return-[] here collapsed both into the same empty state
 * and silently defeated that distinction before the caller ever saw a failure
 * to react to (#6088).
 */
export async function loadJobsForTenant(_tenantId) {
  const data = await azureGet('/api/engine-jobs-read');
  return Array.isArray(data?.jobs) ? data.jobs : [];
}

/**
 * Load ALL jobs — super-admin view across every scope and tenant.
 * Pass `env` to target a specific environment's Function App.
 *
 * Deliberately does not catch — see loadJobsForTenant above; the super-admin
 * Engine tab needs the same "failed to load" vs. "genuinely empty" distinction.
 */
export async function loadAllJobs(env) {
  const data = env
    ? await azureEnvGet(env, '/api/engine-jobs-read')
    : await azureGet('/api/engine-jobs-read');
  return Array.isArray(data?.jobs) ? data.jobs : [];
}

/**
 * Save (upsert) a job. Pass `env` to target a specific environment.
 *
 * Returns `{ ok, job, conflict, error }`:
 *   - ok: true                      — the write landed; `job` is the server's copy
 *                                     (carrying a fresh `_jobVersion`).
 *   - ok: false, conflict: true     — someone else saved this job since it was
 *                                     read (HTTP 409). The caller MUST surface
 *                                     this: the user's edit did not land, and
 *                                     silently swallowing it is the bug this
 *                                     return contract exists to prevent (#5067).
 *   - ok: false, conflict: false    — any other failure; `job` is echoed back
 *                                     unchanged, matching the previous
 *                                     best-effort behaviour.
 *
 * `job._jobVersion` (set by engine-jobs-read) is sent as-is so the server can
 * make the write conditional on it.
 */
export async function saveJob(job, env) {
  if (!job || !job.id) return { ok: false, job, conflict: false, error: 'Missing job id' };
  try {
    const res = env
      ? await azureEnvPost(env, '/api/engine-jobs-write', { job })
      : await azurePost('/api/engine-jobs-write', { job });
    return { ok: true, job: res?.job || job, conflict: false, error: null };
  } catch (err) {
    const conflict = err?.httpStatus === 409;
    return {
      ok: false,
      job,
      conflict,
      error: err?.message || 'Failed to save job',
    };
  }
}

/**
 * Delete a job by id. Pass `env` to target a specific environment.
 *
 * Returns `{ ok, error }` — the same "never swallow a failed write" contract
 * `saveJob` above carries (#5067). A delete that fails (401 after a session
 * expiry, 409, 5xx, offline) MUST be surfaced: the caller reloads the list
 * afterwards, so a swallowed failure leaves the job still rendered with no
 * explanation, and the user believes the automation is gone when it is not.
 */
export async function deleteJob(jobId, env) {
  if (!jobId) return { ok: false, error: 'Missing job id' };
  try {
    if (env) {
      await azureEnvDelete(env, '/api/engine-jobs-write', { id: jobId });
    } else {
      await azureFetch(`/api/engine-jobs-write?id=${encodeURIComponent(jobId)}`, {
        method: 'DELETE',
      });
    }
    return { ok: true, error: null };
  } catch (err) {
    return { ok: false, error: err?.message || 'Failed to delete job' };
  }
}

/**
 * Create a new job object (not yet saved).
 */
export function createJobTemplate(overrides = {}) {
  return {
    id: 'job_' + generateId(),
    name: '',
    description: '',
    scope: 'tenant',
    tenantId: null,
    visibleToTenant: false,
    enabled: true,
    isTemplate: false,
    trigger: {
      type: 'schedule',
      schedule: 'daily',
      condition: { entity: 'document', field: 'nextReview', operator: 'overdue_by', value: 0 },
      event: 'document.saved',
    },
    action: {
      type: 'state_logic',
      operation: 'create_task_if_missing',
      config: {
        taskTitle: 'Review: {{entity.title}}',
        taskDescription: 'Auto-generated by engine job.',
        taskType: 'automatic',
        dueDays: 14,
        linkToEntity: true,
        targetField: 'status',
        newValue: 'overdue',
      },
    },
    logRuns: true,
    logOnlyIfChanged: true,
    createdAt: now(),
    lastRunAt: null,
    lastRunResult: null,
    ...overrides,
  };
}

// ── Built-in templates (seeded by super-admin) ────────────────────────────────

export const BUILT_IN_TEMPLATES = [
  {
    id: 'tpl_doc_review',
    name: 'Auto-create overdue document review task',
    description:
      "When a document's review date passes, automatically create a review task if one doesn't already exist.",
    scope: 'superadmin',
    tenantId: null,
    visibleToTenant: true,
    enabled: true,
    isTemplate: true,
    trigger: {
      type: 'condition',
      schedule: 'daily',
      condition: { entity: 'document', field: 'nextReview', operator: 'overdue_by', value: 0 },
      event: null,
    },
    action: {
      type: 'state_logic',
      operation: 'create_task_if_missing',
      config: {
        taskTitle: 'Review required: {{entity.title}}',
        taskDescription: "This document's review date has passed. Please review and update.",
        taskType: 'automatic',
        dueDays: 14,
        linkToEntity: true,
      },
    },
    logRuns: true,
    logOnlyIfChanged: true,
    createdAt: '2026-01-01T00:00:00Z',
    lastRunAt: null,
    lastRunResult: null,
  },
  {
    id: 'tpl_task_overdue',
    name: 'Mark tasks overdue (retired)',
    // Retired (GH #7704): "overdue" is derived from the due date wherever it
    // is shown, never stored. A stored 'overdue' put the task outside the one
    // transition table, and the engine now refuses to write it — so this
    // template would do nothing while saying it did.
    description:
      'Retired — a task is shown as overdue from its due date; its status is never changed to overdue.',
    // Kept for the super-admin template rows already seeded from it, but
    // never offered as a starting point: a job cloned from it would do
    // nothing while saying it did — the reason it was retired.
    retired: true,
    scope: 'superadmin',
    tenantId: null,
    visibleToTenant: true,
    enabled: false,
    isTemplate: true,
    trigger: {
      type: 'condition',
      schedule: 'daily',
      condition: { entity: 'task', field: 'dueDate', operator: 'overdue_by', value: 0 },
      event: null,
    },
    action: {
      type: 'state_logic',
      operation: 'update_status',
      config: {
        targetEntity: 'task',
        targetField: 'status',
        newValue: 'overdue',
      },
    },
    logRuns: true,
    logOnlyIfChanged: true,
    createdAt: '2026-01-01T00:00:00Z',
    lastRunAt: null,
    lastRunResult: null,
  },
  {
    id: 'tpl_doc_saved_task',
    name: 'Create task when document is saved',
    description: 'Every time a document is saved, automatically create a linked review task.',
    scope: 'superadmin',
    tenantId: null,
    visibleToTenant: false,
    enabled: false,
    isTemplate: true,
    trigger: {
      type: 'event',
      schedule: null,
      condition: null,
      event: 'document.saved',
    },
    action: {
      type: 'state_logic',
      operation: 'create_task',
      config: {
        taskTitle: 'Post-save review: {{entity.title}}',
        taskDescription: 'Auto-created after document save.',
        taskType: 'automatic',
        dueDays: 7,
        linkToEntity: true,
      },
    },
    logRuns: false,
    logOnlyIfChanged: false,
    createdAt: '2026-01-01T00:00:00Z',
    lastRunAt: null,
    lastRunResult: null,
  },
  {
    id: 'tpl_vendor_dpa_expiring',
    name: 'Vendor DPA expiring soon',
    description:
      "Daily check — when any vendor's Data Processing Agreement (DPA) expires within the next 60 days, automatically create a renewal review task assigned to the vendor owner. Skips vendors that already have an open task from this job.",
    scope: 'superadmin',
    tenantId: null,
    visibleToTenant: true,
    enabled: true,
    isTemplate: true,
    trigger: {
      type: 'condition',
      schedule: 'daily',
      condition: { entity: 'vendor', field: 'dpaExpiry', operator: 'expires_within', value: 60 },
      event: null,
    },
    action: {
      type: 'state_logic',
      operation: 'create_task_if_missing',
      config: {
        taskTitle: 'Renew DPA: {{entity.name}}',
        taskDescription:
          'The Data Processing Agreement (DPA) for this vendor is approaching its expiry date. Review the vendor record, confirm renewal terms with the vendor, and update the DPA fields (status, signed date, expiry, version) once a new DPA is in place.',
        taskType: 'simple-review',
        dueDays: 30,
        linkToEntity: true,
      },
    },
    logRuns: true,
    logOnlyIfChanged: true,
    createdAt: '2026-04-29T00:00:00Z',
    lastRunAt: null,
    lastRunResult: null,
  },
  {
    id: 'tpl_vendor_assessment_due_soon',
    name: 'Vendor assessment due soon',
    description:
      "Daily check — when any vendor's next-assessment date is within the next 30 days, automatically create a review task assigned to the vendor owner. Skips vendors that already have an open task from this job.",
    scope: 'superadmin',
    tenantId: null,
    visibleToTenant: true,
    enabled: true,
    isTemplate: true,
    trigger: {
      type: 'condition',
      schedule: 'daily',
      condition: {
        entity: 'vendor',
        field: 'nextAssessmentDate',
        operator: 'expires_within',
        value: 30,
      },
      event: null,
    },
    action: {
      type: 'state_logic',
      operation: 'create_task_if_missing',
      config: {
        taskTitle: 'Vendor assessment due: {{entity.name}}',
        taskDescription:
          "This vendor's next-assessment date is approaching. Open the vendor record, run through the 27-question assessment, and update the last-assessment / next-assessment dates once complete.",
        taskType: 'simple-review',
        dueDays: 30,
        linkToEntity: true,
      },
    },
    logRuns: true,
    logOnlyIfChanged: true,
    createdAt: '2026-04-29T00:00:00Z',
    lastRunAt: null,
    lastRunResult: null,
  },
  {
    id: 'tpl_vendor_subprocessors_changed',
    name: 'Sub-processor change — controller notification',
    description:
      "When a vendor's sub-processor list changes (added, removed, or modified), automatically create a task to notify affected controllers. GDPR Art. 28(2) flow-down obligation.",
    scope: 'superadmin',
    tenantId: null,
    visibleToTenant: true,
    enabled: false,
    isTemplate: true,
    trigger: {
      type: 'event',
      schedule: null,
      condition: null,
      event: 'vendor.subprocessors_changed',
    },
    action: {
      type: 'state_logic',
      operation: 'create_task_if_missing',
      config: {
        taskTitle: 'Notify controllers — sub-processor change for {{entity.name}}',
        taskDescription:
          'The sub-processor list for this vendor has changed. Under GDPR Art. 28(2) the processor must inform controllers of the change in a timely manner to give them the opportunity to object. Confirm the affected controllers and dispatch the notification.',
        taskType: 'simple-review',
        dueDays: 14,
        linkToEntity: true,
      },
    },
    logRuns: true,
    logOnlyIfChanged: false,
    createdAt: '2026-04-29T00:00:00Z',
    lastRunAt: null,
    lastRunResult: null,
  },
  {
    id: 'tpl_vendor_registry_sync',
    name: 'Monthly vendor registry sync',
    description:
      "Every month, refresh company data from Nordic business registries (CVR, BRREG, PRH) for all active vendors with a registry ID. If a vendor's risk score worsens, a review task is automatically assigned to the vendor owner.",
    scope: 'superadmin',
    tenantId: null,
    visibleToTenant: true,
    enabled: true,
    isTemplate: true,
    trigger: {
      type: 'schedule',
      schedule: 'monthly',
      condition: null,
      event: null,
    },
    action: {
      type: 'vendor_sync',
      operation: 'vendor_registry_sync',
      config: {},
    },
    logRuns: true,
    logOnlyIfChanged: false,
    createdAt: '2026-01-01T00:00:00Z',
    lastRunAt: null,
    lastRunResult: null,
  },
];

// ── Run log ───────────────────────────────────────────────────────────────────

/**
 * Write a single completed run-log entry. Idempotent on entry.id.
 */
export async function writeRunLog(entry) {
  if (!entry || !entry.id || !entry.jobId || !entry.startedAt) return;
  try {
    await azurePost('/api/engine-run-log-write', entry);
  } catch (_err) {
    // best-effort — UI must not break on a logging failure
  }
}

/**
 * Load run-log entries. Filters: { jobId, tenantId, limit, env }.
 * Tenant-scoped server-side; super-admin sees all. Pass `env` to target a
 * specific environment's Function App.
 */
export async function loadRunLog({ jobId, tenantId, limit, env } = {}) {
  try {
    const params = {
      ...(jobId ? { jobId } : {}),
      ...(tenantId ? { tenantId } : {}),
      ...(limit ? { limit } : {}),
    };
    const data = env
      ? await azureEnvGet(env, '/api/engine-run-log-read', params)
      : await azureGet('/api/engine-run-log-read', params);
    return Array.isArray(data?.entries) ? data.entries : [];
  } catch (_err) {
    return [];
  }
}

// ── Server-side engine status (Phase 2) ───────────────────────────────────

/**
 * Read the server-side engine status — the 5 Azure Timer Trigger functions
 * with their latest run from engine_run_log. Super-admin only.
 *
 * @param {object} opts
 * @param {boolean} opts.includeRuns — when true, each server returns up to 20 recent runs
 * @param {string} opts.env — target a specific environment's Function App
 * @returns {Promise<{servers: Array}>}
 */
export async function loadServerEngineStatus({ includeRuns = false, env } = {}) {
  try {
    const params = includeRuns ? { includeRuns: 1 } : {};
    const data = env
      ? await azureEnvGet(env, '/api/engine-status', params)
      : await azureGet('/api/engine-status', params);
    return Array.isArray(data?.servers) ? data.servers : [];
  } catch (_err) {
    return [];
  }
}

/**
 * Is the production alerting actually configured? (super-admin only)
 *
 * RISK-1508: the action group, the alert rules and the availability test are
 * Azure resources created by hand, so nothing in the product could tell a
 * configured alert from a deleted one — and that failure is silent, because a
 * rule that matches nothing produces no alerts, which is what a healthy system
 * also produces. This reconciles a manifest against what Azure reports.
 *
 * Read-only by construction (SVCOPS-B-005): there is no enable/disable
 * counterpart here or on the endpoint, and the identity behind it holds Reader
 * rather than Monitoring Contributor. A switch that could silence alerting,
 * living inside the system alerting watches, is the failure SVCOPS-B-001
 * refuses.
 *
 * Deliberately does NOT catch-and-return-a-clean-shape. A failed request must
 * reach the caller so the panel can say "could not determine" — swallowing it
 * into a default object is exactly how "I could not check" becomes a green
 * tick, which is the one thing this feature exists to prevent. Same reasoning
 * as loadJobsForTenant above (#6088), with a sharper consequence.
 */
export async function loadAlertingStatus({ env } = {}) {
  return env
    ? await azureEnvGet(env, '/api/alerting-status')
    : await azureGet('/api/alerting-status');
}

/**
 * Manually trigger a server-side timer (super-admin only).
 * Pass `env` to target a specific environment's Function App.
 * Resolves with { ok, result, summary, details, runId } on success,
 * or { error, retryAfter? } on failure (cooldown / forbidden / etc).
 */
export async function triggerServerJob(jobId, env) {
  if (!jobId) return { error: 'Missing jobId' };
  try {
    if (env) {
      return await azureEnvPost(env, '/api/engine-job-trigger', { jobId });
    }
    const res = await azureFetch('/api/engine-job-trigger', {
      method: 'POST',
      body: { jobId },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        error: json.error || `HTTP ${res.status}`,
        retryAfter: json.retryAfter,
        status: res.status,
      };
    }
    return json;
  } catch (err) {
    return { error: err.message || 'Network error' };
  }
}
